import { Flex, Text, useCurrentBreakpoint } from '@app/muiplus'
import Close from '@mui/icons-material/Close'
import Delete from '@mui/icons-material/Delete'
import ModeEdit from '@mui/icons-material/ModeEdit'
import { DialogContent, DialogTitle, IconButton, Popover } from '@mui/material'
import { Box } from '@mui/system'
import { useCallback, useContext } from 'react'
import { usePermission } from 'src/services/usePermission'
import { CalendarContext } from './CalendarContext'
import { CalendarDisplayEvent } from './CalendarDisplayEvent'
import { parseCalendarEvent } from './calendarUtils'

interface CalendarDetailsProps {
	anchorEl: HTMLElement | null
	displayEvent: CalendarDisplayEvent | undefined
	onClose: () => void
}
export function CalendarDetails(props: CalendarDetailsProps) {
	let { displayEvent, onClose, anchorEl } = props
	const { editEvent, deleteEvent } = useContext(CalendarContext)
	const event = displayEvent?.event

	const perm = usePermission()
	const bkp = useCurrentBreakpoint()

	const edit = useCallback(() => {
		if (!event) return null
		editEvent(event)
		onClose()
	}, [editEvent, event, onClose])

	const internalDeleteEvent = useCallback(() => {
		if (!event) return null
		deleteEvent(event)
		onClose()
	}, [deleteEvent, event, onClose])

	const titleProps = bkp == 'mobile' ? { fontSize: '0.7em', lineHeight: '1.3em' } : { fontSize: '1em' }

	const getDetails = useCallback(() => {
		if (!event) return null
		const contentProps = bkp == 'mobile' ? { fontSize: '0.8em', lineHeight: '1.3em' } : { fontSize: '1em' }
		const { dateStr, startTime, tooltip } = parseCalendarEvent(event)

		let res = <Text>{dateStr}</Text>
		let desc = null
		if (event.description) desc = <Text>{event.description}</Text>
		return (
			<Box {...contentProps}>
				{res}
				{desc}
			</Box>
		)
	}, [bkp, event])

	if (!event) return null

	const editButton = perm.isCalendarEditor ? (
		<IconButton onClick={edit}>
			<ModeEdit />
		</IconButton>
	) : null
	const deleteButton = perm.isCalendarEditor ? (
		<IconButton onClick={internalDeleteEvent}>
			<Delete />
		</IconButton>
	) : null

	return (
		<Popover
			open={Boolean(event)}
			elevation={24}
			onClose={onClose}
			slotProps={{ paper: { sx: { borderRadius: '1em' } } }}
			sx={{
				borderRadius: '1000%'
				// maxWidth: '1000px'
			}}
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'center'
			}}
			transformOrigin={{
				vertical: 'bottom',
				horizontal: 'center'
			}}
		>
			<DialogTitle>
				<Flex justifyContent="space-between" {...titleProps}>
					<Text>{event.summary}</Text>
					<Flex alignItems="start">
						{editButton}
						{deleteButton}
						<IconButton onClick={onClose}>
							<Close />
						</IconButton>
					</Flex>
				</Flex>
			</DialogTitle>

			<DialogContent
				sx={{
					maxWidth: '800px'
				}}
			>
				{getDetails()}
			</DialogContent>
		</Popover>
	)
}
